// Start screen: three ornate leader cards from world.perspectives(). Each shows the leader's
// medallion, title, term and two verbatim agenda quotes with speaker, document and date.
// Cards are sized to the viewport; long quotes are cut at a word boundary with " […]" and
// expand on click; excerpts that start or stop mid-sentence carry an ellipsis (QuoteText.tsx).
// A card's quotes are shortened until both fit its agenda box, so no attribution is faded out.

import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { AgendaItem, LeaderId } from '../core/schema.ts';
import { FICTION_ROUTE } from '../services/fiction/api.ts';
import { useServices } from './context.ts';
import { perspectiveDetail } from './extensions.ts';
import { fmtDate, fmtDateLong } from './format.ts';
import { Icon } from './icons.tsx';
import { uniqueKeys } from './keys.ts';
import { leaderEmblem, lookOf } from './nation.ts';
import { Frame, Medallion, SourceChip } from './parts.tsx';
import { QuoteText } from './QuoteText.tsx';
import './fiction/door.css';

const AGENDA_SHOWN = 2;
const FICTION_HREF = `?${FICTION_ROUTE.param}=${FICTION_ROUTE.value}`;
const FICTION_NOTE = 'Fork the real world. Everything after it is simulated.';
const MEDAL_SIZE = 132;
const QUOTE_CHARS = 150;
const QUOTE_MIN = 60;
const QUOTE_STEP = 15;

/**
 * Quote length that lets the agenda fit its box: starts at QUOTE_CHARS and shrinks a step per
 * render while the content overflows (Xi's two-line title needs ~15 fewer characters than
 * Trump's one-line one). `clipped` is true when even the shortest quotes overflow. A resize, or
 * the web fonts arriving, starts over.
 */
function useFit(box: { current: HTMLElement | null }): { limit: number; clipped: boolean } {
  const [limit, setLimit] = useState(QUOTE_CHARS);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) {
      return;
    }

    const over = el.scrollHeight > el.clientHeight + 1;
    if (over && limit > QUOTE_MIN) {
      setLimit(limit - QUOTE_STEP);
      return;
    }
    if (over !== clipped) {
      setClipped(over);
    }
  });

  useEffect(() => {
    const restart = () => {
      setLimit(QUOTE_CHARS);
      setClipped(false);
    };
    window.addEventListener('resize', restart);
    document.fonts?.ready.then(restart);
    return () => window.removeEventListener('resize', restart);
  }, []);

  return { limit, clipped };
}

export function LeaderSelect() {
  const { world, actions, state } = useServices();
  const leaders = world.perspectives();

  // Keys 1–3 pick a leader.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const i = Number(e.key) - 1;
      if (Number.isInteger(i) && i >= 0 && i < leaders.length) {
        actions.chooseLeader(leaders[i].id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [leaders]);

  return (
    <div class="wc-modal wc-select">
      <div class="wc-modal-bg wc-select-bg" />
      <header class="wc-select-head">
        <div class="wc-select-kicker">WorldCiv</div>
        <h1 class="wc-title wc-select-title">Choose your Leader</h1>
        <div class="wc-select-sub">
          The world as of <b>{fmtDateLong(state.asOf.value)}</b> · one turn is one week · every fact is sourced
        </div>
      </header>
      <a class="wcf-door" href={FICTION_HREF} title={FICTION_NOTE}>
        <span class="wcf-door-title">What-if sandbox</span>
        <span class="wcf-door-note">{FICTION_NOTE}</span>
      </a>
      <div class="wc-select-cards">
        {leaders.map((p, i) => (
          <LeaderCard key={p.id} id={p.id} name={p.name} title={p.title} iso3={p.iso3} hotkey={i + 1} />
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  id: LeaderId;
  name: string;
  title: string;
  iso3: string;
  hotkey: number;
}

function LeaderCard({ id, name, title, iso3, hotkey }: CardProps) {
  const { world, actions } = useServices();
  const look = lookOf(world, iso3);
  const detail = perspectiveDetail(world, id);
  const agenda = detail.agenda.slice(0, AGENDA_SHOWN);
  const agendaKeys = uniqueKeys(agenda.map((a) => a.title + a.date));
  const persp = world.perspectives().find((p) => p.id === id);
  const agendaBox = useRef<HTMLDivElement>(null);
  const { limit, clipped } = useFit(agendaBox);

  return (
    <div class="wc-select-card" style={{ '--nation': look.colors.primary }}>
      <Frame large="large" class="wc-select-frame">
        <div class="wc-select-medal">
          {persp ? <Medallion {...leaderEmblem(world, persp)} size={MEDAL_SIZE} /> : null}
        </div>
        <div class="wc-select-nation">{look.name}</div>
        <h2 class="wc-select-name">{name}</h2>
        <div class="wc-select-role">{title}</div>
        {detail.term ? (
          <div class="wc-select-term">
            In office since <b>{fmtDate(detail.term.from)}</b> <SourceChip ids={detail.term.sources} />
          </div>
        ) : (
          <div class="wc-select-term wc-empty">Term not provided by the world service</div>
        )}

        <div class="wc-rule-title wc-select-agenda-title">Agenda</div>
        <div class={clipped ? 'wc-select-agenda wc-select-agenda--clipped' : 'wc-select-agenda'} ref={agendaBox}>
          {agenda.length === 0 ? <div class="wc-empty">No sourced agenda items yet</div> : null}
          {agenda.map((a, i) => (
            <AgendaQuote key={agendaKeys[i]} item={a} limit={limit} />
          ))}
        </div>

        <button type="button" class="wc-btn wc-btn--gold wc-select-go" onClick={() => actions.chooseLeader(id)}>
          <span>Play as {name}</span>
          <Icon name="next" size={16} />
        </button>
        <div class="wc-select-key">Press {hotkey}</div>
      </Frame>
    </div>
  );
}

export function AgendaQuote({ item, limit = QUOTE_CHARS }: { item: AgendaItem; limit?: number }) {
  return (
    <figure class="wc-agenda wc-parchment">
      <div class="wc-agenda-title">{item.title}</div>
      <blockquote class="wc-quote wc-agenda-quote">
        <QuoteText quote={item.quote} limit={limit} />
      </blockquote>
      <figcaption class="wc-agenda-cite">
        <span>
          — {item.speaker}, <i>{item.document}</i>, {fmtDate(item.date)}
        </span>
        <SourceChip ids={item.sources} />
      </figcaption>
    </figure>
  );
}
